import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ComponentProps, ReactNode } from "react";

// 지도 위 장례식장 핀 카드(E4 B-3). 같은 동네에서도 무빈소가 되는 곳과 안 되는 곳이 갈린다는 것을 보여 준다.
// 핀 하나 = 말풍선(가능/불가) → 건물 아이콘(+ 상태 배지) → 이름. 장례식장마다 다른 건물 아이콘을 쓴다.
// 지도는 그림이다(실제 위치 아님). 레퍼런스: uibowl 「지도·내주변」 핀, 후루츠패밀리 품절 흐림 처리.

export interface MubinsoMapPin {
  icon: ComponentProps<typeof ColorIcon>["name"];
  /** 핀 아래 이름(예: "A 장례식장") */
  label: string;
  available: boolean;
  /** 말풍선 글. 없으면 「무빈소 가능」 / 「불가」 (C-2 는 가격을 넣는다) */
  bubble?: string;
  /** 지도 가로 위치(%). 핀 가운데 기준 */
  x: number;
  /** 지도 세로 위치(px). 말풍선 위쪽 기준 */
  y: number;
}

/** 360px 폭에서 핀 2줄(핀 높이 약 95px)이 위아래 여백을 두고 들어가는 높이 */
const MAP_HEIGHT = 226;

export function MubinsoMapPinCard({
  title,
  description,
  pins,
  mapVariant = "a",
  footer,
}: {
  title: string;
  description?: ReactNode;
  pins: MubinsoMapPin[];
  /** 지도 그림. 같은 고객이 두 지도를 볼 수 있어 화면마다 길 모양을 다르게 쓴다(B-3: a, C-2: b) */
  mapVariant?: "a" | "b";
  /** 카드 제목 바로 아래 체크 한 줄(C-2 「평균 이용료」, 주완 2026-10-30) */
  footer?: string;
}) {
  return (
    <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20}>
      <Box className="body_2_normal_sb gray900">{title}</Box>
      {/* 화장장 카드(C-3)의 체크 줄과 같은 모양(주완 2026-10-30) */}
      {footer && (
        <>
          <Spacing gap={10} />
          <CardCheckLine>{footer}</CardCheckLine>
        </>
      )}
      {description && (
        <>
          <Spacing gap={6} />
          <Box className="body_14_r gray700" style={{ wordBreak: "keep-all" }}>
            {description}
          </Box>
        </>
      )}
      <Spacing gap={12} />
      <Box
        position="relative"
        width="100%"
        height={`${MAP_HEIGHT}px`}
        borderRadius="12px"
        overflow="hidden"
        bg={colors.warmGray200}
      >
        {mapVariant === "a" ? <MapBackground /> : <MapBackgroundB />}
        {pins.map((pin) => (
          <MapPin key={pin.label} pin={pin} />
        ))}
      </Box>
    </BaseStack>
  );
}

/** 곡선 큰길 1 · 골목 3 · 공원 2. 십자 도로로 4칸이 나뉘어 보이지 않게 불규칙하게 깐다 */
function MapBackground() {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      viewBox={`0 0 300 ${MAP_HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M-10 196 C 60 180, 110 150, 150 118 S 240 56, 320 32"
        stroke={colors.white}
        strokeWidth="14"
        fill="none"
      />
      <path
        d="M40 -10 C 60 48, 70 100, 110 226"
        stroke={colors.warmGray50}
        strokeWidth="7"
        fill="none"
      />
      <path
        d="M210 226 C 220 184, 250 160, 320 150"
        stroke={colors.warmGray50}
        strokeWidth="7"
        fill="none"
      />
      <path
        d="M-10 76 C 80 84, 170 76, 230 94"
        stroke={colors.warmGray50}
        strokeWidth="5"
        fill="none"
      />
      <path
        d="M236 178 c 18 -14 48 -10 58 6 c 8 14 -8 34 -30 34 c -22 0 -44 -22 -28 -40z"
        fill={colors.green100}
        fillOpacity="0.35"
      />
      <path
        d="M8 122 c 10 -8 30 -6 36 4 c 5 9 -4 20 -18 20 c -14 0 -26 -14 -18 -24z"
        fill={colors.green100}
        fillOpacity="0.35"
      />
    </svg>
  );
}

/** 두 번째 지도(C-2). 큰길이 왼쪽 위에서 오른쪽 아래로 지나고, 공원은 오른쪽 위 · 왼쪽 아래에 둔다 */
function MapBackgroundB() {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      viewBox={`0 0 300 ${MAP_HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M-10 34 C 70 52, 130 84, 176 128 S 262 210, 320 222"
        stroke={colors.white}
        strokeWidth="14"
        fill="none"
      />
      <path
        d="M196 -10 C 186 56, 204 120, 184 236"
        stroke={colors.warmGray50}
        strokeWidth="7"
        fill="none"
      />
      <path
        d="M-10 176 C 50 164, 110 182, 182 164"
        stroke={colors.warmGray50}
        strokeWidth="5"
        fill="none"
      />
      <path
        d="M70 236 C 80 206, 76 186, 96 172"
        stroke={colors.warmGray50}
        strokeWidth="5"
        fill="none"
      />
      <path
        d="M236 22 c 18 -12 50 -6 56 12 c 6 18 -14 32 -36 30 c -22 -2 -36 -26 -20 -42z"
        fill={colors.green100}
        fillOpacity="0.35"
      />
      <path
        d="M10 196 c 12 -8 34 -4 38 8 c 4 12 -8 22 -22 20 c -14 -2 -26 -16 -16 -28z"
        fill={colors.green100}
        fillOpacity="0.35"
      />
    </svg>
  );
}

function MapPin({ pin }: { pin: MubinsoMapPin }) {
  const bubbleBg = pin.available ? colors.orange650 : colors.white;
  return (
    <BaseStack
      position="absolute"
      left={`${pin.x}%`}
      top={`${pin.y}px`}
      transform="translateX(-50%)"
      alignItems="center"
    >
      <Box
        className="caption_12_b"
        whiteSpace="nowrap"
        borderRadius="8px"
        padding="4px 8px"
        bg={bubbleBg}
        color={pin.available ? colors.white : colors.gray400}
        boxShadow="0 2px 6px rgba(0, 0, 0, 0.08)"
      >
        {pin.bubble ?? (pin.available ? "무빈소 가능" : "불가")}
      </Box>
      {/* 말풍선 꼬리 */}
      <Box
        width={0}
        height={0}
        borderLeft="5px solid transparent"
        borderRight="5px solid transparent"
        borderTop={`6px solid ${bubbleBg}`}
      />
      <Spacing gap={4} />
      <Box position="relative" width="40px" height="40px">
        {/* 안 되는 곳은 흐린 회색. 원 바탕 없이 아이콘만 */}
        <Box filter={pin.available ? undefined : "grayscale(1)"} opacity={pin.available ? 1 : 0.6}>
          <ColorIcon name={pin.icon} size={40} />
        </Box>
        <StatusBadge available={pin.available} />
      </Box>
      <Spacing gap={2} />
      <Box
        className="caption_12_b"
        whiteSpace="nowrap"
        color={pin.available ? colors.gray900 : colors.gray500}
      >
        {pin.label}
      </Box>
    </BaseStack>
  );
}

/** 건물 오른쪽 아래 상태 배지. 가능은 주황 체크, 불가는 회색 X. 흰 테두리로 건물과 떼어 놓는다 */
function StatusBadge({ available }: { available: boolean }) {
  return (
    <Box
      position="absolute"
      right="-4px"
      bottom="-2px"
      width="16px"
      height="16px"
      borderRadius="50%"
      bg={available ? colors.orange650 : colors.warmGray550}
      boxShadow={`0 0 0 2px ${colors.white}`}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {available ? (
        <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden>
          <path
            d="M2.5 6.2L5 8.5L9.5 3.5"
            stroke={colors.white}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden>
          <path
            d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5"
            stroke={colors.white}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )}
    </Box>
  );
}

/**
 * 권내 · 권외 그림 카드(E4 C-4). 가운데 고인 주소지(집), 둘레의 연한 주황 원이 「권내 지역」.
 * 원 안 화장장 = 권내(주황 말풍선), 원 밖 화장장 = 권외(흰 말풍선). 지도 카드와 같은 회색 바탕 · 핀 모양.
 */
export function MubinsoRegionCircleCard({
  title,
  gloss,
  centerLabel,
  inside,
  outside,
  footer,
}: {
  title: string;
  /** 제목 아래 작은 회색 뜻풀이 */
  gloss?: string;
  centerLabel: string;
  inside: string;
  outside: string;
  /** 제목 아래 체크 한 줄(14px) */
  footer?: string;
}) {
  return (
    <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20}>
      <Box className="body_2_normal_sb gray900">{title}</Box>
      {gloss && (
        <>
          <Spacing gap={4} />
          <Box className="caption_12_m" color={colors.gray500}>
            {gloss}
          </Box>
        </>
      )}
      {/* 체크 줄은 제목 · 뜻풀이 바로 아래(주완 2026-10-30) */}
      {footer && (
        <>
          <Spacing gap={10} />
          <CardCheckLine>{footer}</CardCheckLine>
        </>
      )}
      <Spacing gap={12} />
      <Box
        position="relative"
        width="100%"
        height={`${REGION_HEIGHT}px`}
        borderRadius="12px"
        overflow="hidden"
        bg={colors.warmGray200}
      >
        {/* 바깥에 지도 길을 연하게 깔아 「동네 안 · 밖」 으로 읽히게(주완 2026-10-30) */}
        <Box position="absolute" inset={0} opacity={0.7}>
          <MapBackgroundB />
        </Box>
        {/* 권내 지역: 고인 주소지를 가운데 둔 반투명 주황 원. 원 안에도 지도가 비친다 */}
        <Box
          position="absolute"
          left="50%"
          top="50%"
          width="156px"
          height="156px"
          transform="translate(-50%, -50%)"
          borderRadius="50%"
          bg={colors.orange650}
          opacity={0.12}
        />
        <BaseStack
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
          alignItems="center"
          spacing={2}
        >
          <ColorIcon name="icon_Graphic_Home" size={32} />
          <Box className="caption_12_b" whiteSpace="nowrap" color={colors.gray700}>
            {centerLabel}
          </Box>
        </BaseStack>
        {/* 권내 화장장은 원 안쪽 왼쪽 위, 권외는 원 밖 오른쪽 아래(대각선으로 균형) */}
        <RegionPin label={inside} inside x="36%" y={18} />
        <RegionPin label={outside} x="74%" y={136} />
      </Box>
    </BaseStack>
  );
}

const REGION_HEIGHT = 216;

function RegionPin({
  label,
  inside,
  x,
  y,
}: {
  label: string;
  inside?: boolean;
  x: string;
  y: number;
}) {
  const bubbleBg = inside ? colors.orange650 : colors.white;
  return (
    <BaseStack
      position="absolute"
      left={x}
      top={`${y}px`}
      transform="translateX(-50%)"
      alignItems="center"
    >
      <Box
        className="caption_12_b"
        whiteSpace="nowrap"
        borderRadius="8px"
        padding="4px 8px"
        bg={bubbleBg}
        color={inside ? colors.white : colors.gray600}
        boxShadow="0 2px 6px rgba(0, 0, 0, 0.08)"
      >
        {label}
      </Box>
      <Box
        width={0}
        height={0}
        borderLeft="5px solid transparent"
        borderRight="5px solid transparent"
        borderTop={`6px solid ${bubbleBg}`}
      />
      <Spacing gap={4} />
      <ColorIcon name="icon_Graphic_BuildingRoof" size={32} />
    </BaseStack>
  );
}

/** 카드 제목 아래 주황 체크 + 14px 회색 한 줄(C-2 지도 카드 · C-3 화장장 카드) */
function CardCheckLine({ children }: { children: ReactNode }) {
  return (
    <BaseStack direction="horizontal" alignItems="center" spacing={6}>
      <Box
        flex="none"
        width="18px"
        height="18px"
        borderRadius="50%"
        bg={colors.orange200}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M20 6 9 17l-5-5"
            stroke={colors.orange650}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
      <Box className="body_14_r gray700" sx={{ wordBreak: "keep-all" }}>
        {children}
      </Box>
    </BaseStack>
  );
}

/**
 * 「무빈소가 되는 장례식장 찾기」 지도 카드(B-3 · D-1 공용).
 * 숫자는 실제 데이터: 서울 영등포구 장례식장 8곳(parlors_detail.json, 제외 업체 뺌) 중
 * 고이가 무빈소 진행을 확인한 곳 1곳(mubinsoAvailableParlors.ts), 2026-10-30 기준.
 * 서울에서 장례식장이 가장 많은 구라 "많아도 되는 곳은 적다" 가 가장 잘 보인다.
 * 지도는 그림이고 A~D 는 예시다. 회색 핀은 "불가"(주완 2026-10-30: 고이가 알아본 결과라서)
 */
export function MubinsoParlorShareMapCard() {
  return (
    <MubinsoMapPinCard
      title="무빈소가 되는 장례식장 찾기"
      description={
        <>
          {/* 첫 줄(208px)이 둘째 줄(181px)보다 길게: 제목 → 첫 줄 → 둘째 줄이 계단처럼 길어지면 시선이 흐른다 */}
          서울 영등포구에 있는 장례식장 <b style={{ color: colors.gray900 }}>8곳</b> 중
          <br />
          무빈소가 되는 곳은 <b style={{ color: colors.orange650, fontWeight: 900 }}>단 1곳</b>
          이에요.
        </>
      }
      pins={[
        { icon: "icon_Graphic_Building", label: "A 장례식장", available: true, x: 28, y: 16 },
        {
          icon: "icon_Graphic_BuildingLow",
          label: "B 장례식장",
          available: false,
          x: 80,
          y: 28,
        },
        {
          icon: "icon_Graphic_BuildingRoof",
          label: "C 장례식장",
          available: false,
          x: 22,
          y: 122,
        },
        {
          icon: "icon_Graphic_BuildingAnnex",
          label: "D 장례식장",
          available: false,
          x: 60,
          y: 126,
        },
      ]}
    />
  );
}
